import {
  searchCustomerCode,
  searchHubWithrealFilter,
  searchShelfNumber,
  getAddressHierarchyData,
  getPincodeList,
} from '@api/dashboard';
import { searchCustomers } from '@api/customerManagement';
import { tmsBagExceptionSearch } from '@api/TMSDashboard/genericTMSView';
import { searchFilteredRiderCode } from '@api/genericAnalyticsView';
import { searchZone } from '@api/genericConsignmentView';
import {
  getAllVehicleList,
  getAllWorkerList,
  getAllHelperList,
  getFullFlmRouteMasterList,
  searchTripReference,
} from 'src/api/trips';
import { getSearchedVendorDetails } from 'src/api/vendorPayout';
import { tmsSetupSearchRoutes } from '@api/TMSDashboard/Setup';
import { getAllTmsGatesSearch, getAllTmsDocksSearch } from 'src/api/TMSDashboard/VehicleView';
import moment from 'moment';
import { fetchMoveableAssets } from 'src/api/retailDashboard';
import {
  riderSearch,
  riderSearchSimple,
  getHubsWithRiderReconPermission,
} from '@api/riderReconciliation';
import { getThirdPartyReconList } from 'src/api/ThirdPartyReconciliation';
import { searchEmployeeByEmail, fetchMetricTypes } from '../../ControlTower/ControlTower.api';
import { createEventPrettyNameResolver } from 'src/components/common/ObjectStatusPrettyName';

export const buildResolvedConsignmentFilterLists = ({
  existingFilters,
  filtersMetadata,
  eventMetadata,
  t,
  statusSuffix,
}: {
  existingFilters: any[];
  filtersMetadata: any[];
  eventMetadata: any;
  t: (key: string) => string;
  statusSuffix?: string;
}) => {
  const resolvePrettyName = createEventPrettyNameResolver('CONSIGNMENT', eventMetadata, t);

  const prepareFilter = (meta: any, extra: any = {}) => {
    const prepared: any = {
      ...meta,
      ...extra,
      label: t(meta.id) !== meta.id ? t(meta.id) : meta.label,
      placeholder: t(meta.id) !== meta.id ? t(meta.id) : meta.placeholder,
    };
    if (meta.id === 'statusList' && Array.isArray(meta.options)) {
      prepared.options = meta.options.map((option: any) => ({
        ...option,
        label: resolvePrettyName(String(option?.value ?? '').toLowerCase(), false, statusSuffix),
      }));
    }
    return prepared;
  };

  const findMeta = (id: string) => filtersMetadata.filter((item: any) => item.id === id)[0];

  const filtersToShow = existingFilters
    .filter((ele: any) => ele.show_on_main_dashboard)
    .map((ele: any) => findMeta(ele.id))
    .filter(Boolean)
    .map((meta: any) => prepareFilter(meta));

  const moreFiltersToShow = existingFilters
    .filter((ele: any) => !ele.show_on_main_dashboard)
    .map((ele: any) => {
      const meta = findMeta(ele.id);
      return meta
        ? prepareFilter(meta, { show_on_main_dashboard: ele.show_on_main_dashboard })
        : null;
    })
    .filter(Boolean);

  return { filtersToShow, moreFiltersToShow };
};

export const filterFormItemLayout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 19 },
};
export const filterTailLayout = {
  wrapperCol: { span: 24 },
};
export const defaultTokenSeperators = [',', ' '];

export const isInvalidFilter = (value) => {
  if (
    value === null ||
    value === undefined ||
    ((typeof value === 'string' || Array.isArray(value)) && !value.length) ||
    (value instanceof Object && !Object.keys(value).length)
  ) {
    return true;
  }
  return false;
};
export const sanitiseObject = (filters) => {
  const obj = { ...filters };
  for (const i in obj) {
    if (isInvalidFilter(obj[i])) {
      delete obj[i];
    }
  }
  return obj;
};

const customerCodeSearch = async (value) => {
  const response = await searchCustomerCode(value);
  let customerCodes = response.data;
  customerCodes = customerCodes
    ? customerCodes.map((cc: any) => ({
        key: cc.id,
        label: cc.name + ', ' + cc.code,
        data: cc,
      }))
    : [];
  return customerCodes;
};

const parentCustomerSearch = async (value) => {
  const response = await searchCustomers(value);
  let customers = response.data;
  customers = customers
    ? customers.map((c: any) => ({
        key: c.code,
        label: `${c.name}, ${c.code}`,
        data: c,
      }))
    : [];
  return customers;
};

const areaPincodeSearch = async (value) => {
  const response = await getPincodeList({ query: value });
  let pincodes = response.data;
  pincodes = pincodes
    ? pincodes.map((p: any) => ({
        key: p.code,
        label: `${p.code}, ${p.district}`,
        data: p,
      }))
    : [];
  return pincodes;
};

const carrierAccountSearch = async (value) => {
  const body = { search_params: value };
  const response = await getThirdPartyReconList(body);
  let options = response.data?.courier_account_details;
  options = options
    ? options.map((cc: any) => ({
        key: cc.courier_account,
        label: cc.courier_account,
        data: cc,
      }))
    : [];
  return options;
};

const shelfNumberSearch = async (value) => {
  const response = await searchShelfNumber(value);
  let shelfNumbers = response.data;
  shelfNumbers = shelfNumbers
    ? shelfNumbers.map((cc: any) => ({
        key: cc.id,
        label: cc.shelfnumber,
        data: cc,
      }))
    : [];
  return shelfNumbers;
};

const exceptionSearch = async (value) => {
  const response = await tmsBagExceptionSearch(value);
  let exceptions = response.data;
  exceptions = exceptions
    ? exceptions.map((cc: any) => ({
        key: cc.bag_exception_id,
        label: `${cc.bag_exception_name} (${cc.bag_exception_code})`,
        data: cc,
      }))
    : [];
  return exceptions;
};

const hubSearch = ({
  hubType,
  realFilter = true,
  keyField = 'id',
}: {
  hubType?: string;
  realFilter?: boolean;
  keyField?: 'id' | 'code';
}) => async (value) => {
  const response = await searchHubWithrealFilter(realFilter)(value, hubType);
  const hubs = response.data;
  return hubs
    ? hubs.map((hub: any) => ({
        key: hub[keyField],
        label: `${hub.name}, ${hub.code}`,
        data: hub,
      }))
    : [];
};

const riderCodeSearch = async (value, hubIds?: any) => {
  const response = await searchFilteredRiderCode(value, hubIds);
  const riderCodes = response.data
    ? response.data.map((rider) => {
        return { key: rider.id, label: `${rider.name}, ${rider.code}`, data: rider };
      })
    : [];
  return riderCodes;
};

const zoneCodeSearch = async (value) => {
  const params = { q: value };
  const response = await searchZone(params);
  let zoneCodes = response.data;
  zoneCodes = zoneCodes
    ? zoneCodes.map((zone: any) => ({
        key: zone.node_code,
        label: `${zone.node_name}, ${zone.node_code}`,
        data: zone,
      }))
    : [];
  return zoneCodes;
};

const workerCodeSearch = async (value, hubId) => {
  const response = await getAllWorkerList(hubId, value);
  const workers = response.data?.data;
  const workerCodes = workers
    ? workers.map((worker) => {
        return { key: worker.worker_code, label: `${worker.worker_name}, ${worker.worker_code}`, data: worker };
      })
    : [];
  return workerCodes;
};

const workerIdSearch = async (value, hubId) => {
  const response = await getAllWorkerList(hubId, value);
  const workers = response.data?.data;
  const workerIds = workers
    ? workers.map((worker) => {
        return { key: worker.worker_id, label: `${worker.worker_name}, ${worker.worker_code}`, data: worker };
      })
    : [];
  return workerIds;
};

const vehicleCodeSearch = async (value, hubId) => {
  const response = await getAllVehicleList(hubId, value);
  const vehicleCodes = response.data
    ? response.data.map((vehicle) => {
        return { key: vehicle.vehicle_id, label: `${vehicle.vehicle_number}, ${vehicle.vehicle_code}`, data: vehicle };
      })
    : [];
  return vehicleCodes;
};

const helperCodeSearch = async (value, hubId) => {
  const response = await getAllHelperList({ hubId, queryStr: value });
  const helpers = response.data
    ? response.data.map((worker) => {
        return { key: worker.worker_id, label: `${worker.worker_name}, ${worker.worker_code}`, data: worker };
      })
    : [];
  return helpers;
};

const vehicleVendorSearch = async (value) => {
  const response = await getSearchedVendorDetails({ str: value });
  const vehicleVendors = response.data
    ? response.data.map((vendor) => {
      return { key: vendor.id, label: `${vendor.name}, ${vendor.code}`, data: vendor };
      })
    : [];
  return vehicleVendors;
};

const vendorNameSearch = async (value) => {
  const response = await getSearchedVendorDetails({ str: value });
  const vendors = response.data
    ? response.data.map((vendor) => {
      return { key: vendor.id, label: vendor.name, data: vendor };
      })
    : [];
  return vendors;
};

const tripReferenceSearch = async (value) => {
  const response = await searchTripReference(value);
  const trips = response.data
    ? response.data.map((trip: any) => ({
        key: trip.trip_reference_number,
        label: trip.trip_reference_number,
        data: trip,
      }))
    : [];
  return trips;
};

const routeCodeSearch = async (value) => {
  const response = await tmsSetupSearchRoutes(value);
  const routes = response.data
    ? response.data.map((route) => {
        return {key: route.id, label: `${route.name}, ${route.code}` };
      })
    : [];
  return routes;
};

const flmRouteSearch = async (value, hubId) => {
  const response = await getFullFlmRouteMasterList({ hubId, routeCode: value });
  const routes = response.data || [];
  return routes.map((route) => {
    return {
      key: route.id,
      label: `${route.name}, ${route.code}`,
    };
  });
};

const gateCodeSearch = async (value, hubId) => {
  const body = {
    hub_id_list: [hubId],
    gate_search_string: value,
  };
  const response = await getAllTmsGatesSearch(body);
  const gateCodes = response?.data?.page_data
    ? response.data.page_data.map((gate) => {
        return { key: gate.gate_id, label: `${gate.gate_name}, ${gate.gate_code}` };
      })
    : [];
  return gateCodes;
};

const dockCodeSearch = async (value, hubId) => {
  const body = {
    hub_id_list: [hubId],
    docking_station_search_string: value,
  };
  const response = await getAllTmsDocksSearch(body);
  const dockCodes = response?.data?.page_data
    ? response.data.page_data.map((dock) => {
        return { key: dock.dock_id, label: `${dock.dock_name}, ${dock.dock_code}` };
      })
    : [];
  return dockCodes;
};

const moveableAssetSearch = async (value) => {
  const response = await fetchMoveableAssets({ code: value });
  const moveableAssets = response.data
    ? response.data.map((moveableAsset) => {
      return { key: moveableAsset.id, label: moveableAsset.code, data: moveableAsset };
      })
    : [];
  return moveableAssets;
};

const riderListSearch = async (value) => {
  const params = {
    current_page: 1,
    sort_by: 'worker_name',
    q: value,
    called_from: 'RiderList',
  };
  const response = await riderSearch(params);
  return response?.data?.page_data?.map((rider: any) => ({
    key: rider.id,
    label: `${rider.name}, ${rider.worker_code}`,
    data: rider,
  })) || [];
};

const reconRiderSearch = async (value) => {
  const params = {
    current_page: 1,
    sort_by: 'worker_name',
    q: value,
    called_from: 'ReconScreenV2',
  };
  const response = await riderSearchSimple(params);
  return response?.data?.map((rider: any) => ({
    key: rider.id,
    label: `${rider.name}, ${rider.worker_code}`,
    data: rider,
  })) || [];
};

const reconHubSearch = async (value) => {
  const response = await getHubsWithRiderReconPermission({ searchQuery: value });
  return response?.data?.map((hub: any) => ({
    key: hub.id,
    label: `${hub.name}, ${hub.code}`,
    data: hub,
  })) || [];
};

const searchEmployee = async (value: string) => {
  const response = await searchEmployeeByEmail(value);
  return response.isSuccess && response.data
    ? response.data?.searchEmplyoeeEmails?.map((emp) => ({
        key: emp.empId,
        // Users search by email, so surface it in the label: "email (name)".
        label: emp.name ? `${emp.email} (${emp.name})` : emp.email,
      }))
    : [];
};

const metricTypeSearch = async (value: string) => {
  const response = await fetchMetricTypes(value);
  return response.isSuccess && response.data
    ? response.data?.metricTypes?.map((m) => ({
        key: m.metricType,
        label: m.displayName,
      }))
    : [];
};

const countrySearch = async (value) => {
  const response = await getAddressHierarchyData({
    location_key: 'country',
    ...(value ? { code: value } : {}),
  });
  const countries = response.data
    ? response.data.map((country: any) => ({
        key: country.code,
        label: country.name,
        data: country,
      }))
    : [];
  return countries;
};

const defaultFunction = (value) => {
  return [];
};

export const searchStrategy = (strategy, searchSelectParams?: { key?: string; label?: string }) => {
  const baseFn = (() => {
    switch (strategy) {
      case 'hub_search':
        return hubSearch({});
      case 'store_hub_search':
        return hubSearch({ hubType: 'upcountry' });
      case 'pod_hub_search':
        return hubSearch({ realFilter: false });
      case 'exception_search':
        return exceptionSearch;
      case 'customer_code_search':
        return customerCodeSearch;
      case 'rider_code_search':
        return riderCodeSearch;
      case 'zone_code_search':
        return zoneCodeSearch;
      case 'shelf_search':
        return shelfNumberSearch;
      case 'worker_code_search':
        return workerCodeSearch;
      case 'worker_id_search':
        return workerIdSearch;
      case 'vehicle_code_search':
        return vehicleCodeSearch;
      case 'helper_code_search':
        return helperCodeSearch;
      case 'trip_vendor_search':
        return vehicleVendorSearch;
      case 'vendor_name_search':
        return vendorNameSearch;
    case 'route_code_search':
      return routeCodeSearch;
    case 'trip_reference_number_search':
      return tripReferenceSearch;
    case 'flm_route_search':
      return flmRouteSearch;
    case 'gate_code_search':
      return gateCodeSearch;
    case 'dock_code_search':
      return dockCodeSearch;
      case 'moveable_asset_search':
        return moveableAssetSearch;
      case 'rider_list_search':
        return riderListSearch;
      case 'recon_rider_search':
        return reconRiderSearch;
      case 'recon_hub_search':
        return reconHubSearch;
      case 'carrier_account_search':
        return carrierAccountSearch;
      case 'search_employee':
        return searchEmployee;
      case 'metric_type_search':
        return metricTypeSearch;
      case 'country_search':
        return countrySearch;
      case 'parent_customer_search':
        return parentCustomerSearch;
      case 'area_pincode_search':
        return areaPincodeSearch;
      case 'parent_hub_search':
        return hubSearch({ hubType: 'dc', keyField: 'code' });
      case 'booking_branch_hub_search':
        return hubSearch({ hubType: 'branch', keyField: 'code' });
      default:
        return defaultFunction;
    }
  })();

  // Wrapper to transform results
  return async (...args: any[]) => {
    const results = await baseFn(...args);

    const selectKey = searchSelectParams?.key;
    if (!selectKey) {
      return results.map((item: any) => ({
        key: item.key,
        label: item.label,
      }));
    }

    const selectLabel = searchSelectParams?.label;
    return results.map((item: any) => {
      const keyValue = item.data?.[selectKey] || item.key;
      const labelValue = selectLabel ? item.data?.[selectLabel] ?? keyValue : keyValue;
      return {
        key: keyValue,
        label: labelValue,
      };
    });
  };
};

// Function to format value based on metadata type
const formatFilterValueByType = (value: any, type: string, mode?: string) => {
  if (!value) return value;

  switch (type) {
    case 'search':
      if (Array.isArray(value)) {
        return value.map(item => item?.key || item);
      }
      return value?.key || value;
    
    case 'select':
      if (Array.isArray(value)) {
        return value.map(item => item?.value || item);
      }
      return value?.value || value;
    
    case 'datetime':
      // For datetime type, convert timestamp array to unix format
      if (Array.isArray(value) && value.length === 2) {
        return value.map(timestamp => moment(timestamp)?.valueOf());
      }

    default:
      // For other types (radio, input, inputnumber), return value as is
      return value;
  }
};

// Generic function to generate request schema from filter metadata
export const generateFiltersPayload = async (appliedFilters, filtersMetadata) => {
  if (!Array.isArray(filtersMetadata) || !filtersMetadata.length) {
    return {};
  }

  const formattedBody = {};

  filtersMetadata.forEach((field: any) => {
    const { id, type, mode } = field;
    
    if (appliedFilters.hasOwnProperty(id)) {
      formattedBody[id] = formatFilterValueByType(appliedFilters[id], type, mode);
    }
  });

  return formattedBody;
};
